import { useEffect } from 'react'
import { TourProvider, useTour, type StepType } from '@reactour/tour'
import { registrar } from '@/lib/demo/telemetry'
import { leadIdActual } from '@/lib/demo/lead-ref'
import { platform } from '@/lib/platform'

/**
 * Recorrido guiado de la instancia compartida (blueprint §B.10.3).
 *
 * `@reactour/tour` llevaba meses instalado y sin usar en ninguna parte. Aquí
 * cumple dos funciones a la vez, y la segunda es la que importa de verdad:
 *
 *   1. Que el visitante no se pierda. Entra a una plataforma de inversión que no
 *      conoce y sin guía lo normal es que mire dos pantallas y se vaya.
 *
 *   2. Que recorra las pantallas que MÁS dicen de su intención. El recorrido lo
 *      lleva por la ficha de un activo, el simulador, la compra y el panel del
 *      emisor — que son cuatro de los ocho eventos puntuados. No es casualidad:
 *      guiar y medir son el mismo movimiento.
 *
 * SOBRE EL TONO, que es una decisión de producto y no de estilo: los textos
 * hablan del producto, nunca del entorno. Nada de "esto es una demostración",
 * "datos de ejemplo" ni "nada es real". Quien mira esto está evaluando si compra
 * la plataforma; cada aclaración de que lo que ve no cuenta es una razón para
 * dejar de mirarla en serio. La experiencia se vende sola o no se vende.
 *
 * El anclaje es por atributo `data-tour`, no por clase de CSS. Las clases cambian
 * cuando alguien retoca el diseño y el recorrido se rompe en silencio; un
 * atributo dedicado deja claro que algo depende de él.
 */

/** Marca un elemento como parada del recorrido: `<div {...paso('cartera')}>`. */
export function paso(nombre: string): { 'data-tour': string } {
  return { 'data-tour': nombre }
}

const sel = (n: string) => `[data-tour="${n}"]`

const marca = platform.brand?.productName ?? 'la plataforma'

const PASOS: StepType[] = [
  {
    selector: sel('bienvenida'),
    content: `Bienvenido a ${marca}. En dos minutos vas a recorrer el ciclo completo: elegir un activo, invertir, cobrar rendimientos y administrarlo del otro lado del mostrador.`,
  },
  {
    selector: sel('catalogo'),
    content:
      'El catálogo de activos tokenizados. Cada ficha trae la composición del activo, el rendimiento proyectado y la documentación legal que lo respalda.',
  },
  {
    selector: sel('proyecto'),
    content:
      'Abrí una ficha para ver la letra chica: estructura de la colocación, historial de dividendos y trazabilidad en cadena.',
  },
  {
    selector: sel('calculadora'),
    content:
      'El simulador estima tu retorno según cuánto pongas y por cuánto tiempo. Probá con una cifra realista: es la forma más rápida de ver si el modelo te cierra.',
  },
  {
    selector: sel('invertir'),
    content:
      'Acá se compra. Pago, emisión del token y comprobante en un solo paso, con su enlace al explorador de bloques.',
  },
  {
    selector: sel('cartera'),
    content:
      'Tu cartera: cuánto tenés de cada activo, cuánto cobraste y qué te queda por reclamar.',
  },
  {
    selector: sel('panel-emisor'),
    content:
      'Este es el panel del emisor: la plataforma vista por quien la opera. Desde acá se publican activos, se controla la colocación, se revisa la identidad de los inversores y se reparten los rendimientos.',
  },
  {
    selector: sel('ciclo-de-vida'),
    content:
      'Cada activo recorre tres etapas y el panel no deja saltárselas: a distribución solo se pasa con la meta de captación cumplida. Esa regla es la diferencia entre un proceso y un formulario.',
  },
  {
    selector: sel('conmutador'),
    // El texto es NEUTRO respecto a la dirección a propósito. Este paso aparece
    // en las dos caras —el recorrido se filtra por lo que hay en pantalla—, así
    // que dar por hecho que se va hacia el panel del emisor sonaría equivocado
    // justo para quien ya está dentro de él.
    content:
      'Y esto es lo que suele decidir la compra: el mismo producto tiene dos caras. Con este botón pasás del panel del emisor —sus proyectos, sus inversores, su recaudación— a la vista del inversor, y de vuelta, sin salir de la sesión.',
  },
]

/**
 * Sella la telemetría del recorrido. Va dentro del proveedor porque necesita
 * `useTour`, y el proveedor no puede ser su propio hijo.
 */
function SeguimientoDelRecorrido() {
  const { isOpen, currentStep, steps } = useTour()

  useEffect(() => {
    if (!isOpen || steps.length === 0) return
    // Recorrer los pasos NO puntúa por sí solo: lo que puntúa es lo que el
    // visitante haga después. Si el paseo diera puntos, todos saldrían
    // calientes y el puntaje dejaría de discriminar.
    //
    // La excepción es llegar al final: terminar el recorrido entero sí dice
    // algo, así que se registra como apertura efectiva.
    //
    // El final se mide contra `steps`, que es la lista REALMENTE mostrada, y no
    // contra el catálogo completo: el recorrido se filtra por lo que hay en
    // pantalla (ver `useRecorrido`), así que en la ficha de un activo son tres
    // pasos y en el panel son cuatro.
    if (currentStep === steps.length - 1 && leadIdActual()) {
      registrar('demo_opened', { via: 'tour_completado' })
    }
  }, [isOpen, currentStep, steps.length])

  return null
}

export function DemoTourProvider({ children }: { children: React.ReactNode }) {
  return (
    <TourProvider
      steps={PASOS}
      // El recorrido se puede saltar en cualquier momento: nada peor que una
      // presentación que te secuestra.
      showCloseButton
      showNavigation
      showBadge={false}
      disableInteraction={false}
      styles={{
        popover: (base) => ({
          ...base,
          borderRadius: 'var(--radius, 0.875rem)',
          padding: '1.25rem 1.5rem',
          maxWidth: 380,
          fontSize: 15,
          lineHeight: 1.55,
        }),
        maskWrapper: (base) => ({ ...base, opacity: 0.72 }),
      }}
    >
      <SeguimientoDelRecorrido />
      {children}
    </TourProvider>
  )
}

/**
 * Abre el recorrido desde cualquier parte (el botón de ayuda, por ejemplo).
 *
 * EL FILTRADO NO ES UN ADORNO. Los nueve pasos no conviven en una sola pantalla:
 * el catálogo y la cartera están en el panel del inversor, la ficha, el simulador
 * y la compra están en el detalle de un activo, y el ciclo de vida está en la
 * cara del emisor. Un recorrido lineal de nueve pasos
 * apuntaría a elementos que no existen todavía, y `@reactour/tour` con un
 * selector que no encuentra deja el globo pegado a la esquina superior
 * izquierda señalando la nada. Se muestra solo lo que está en pantalla, así que
 * el recorrido se adapta a dónde está parado el visitante en vez de romperse.
 */
export function useRecorrido() {
  const { setIsOpen, setCurrentStep, setSteps } = useTour()

  return {
    abrir: () => {
      const presentes = PASOS.filter(
        (p) => typeof p.selector === 'string' && document.querySelector(p.selector) !== null,
      )
      if (presentes.length === 0) return
      setSteps?.(presentes)
      setCurrentStep(0)
      setIsOpen(true)
    },
  }
}

const CLAVE_YA_RECORRIDO = 'platform.demo-tour.v1'

/**
 * Abre el recorrido solo, una vez, la primera vez que el visitante llega al
 * panel.
 *
 * Se recuerda por prospecto y no por navegador: si dos personas entran con
 * enlaces distintos desde la misma máquina —cosa que pasa en una reunión con un
 * proyector— la segunda merece su recorrido igual que la primera.
 *
 * Espera un cuadro antes de abrir. Los anclajes viven dentro de secciones que
 * entran con una animación, y medirlos antes de que terminen de montarse
 * devuelve rectángulos vacíos.
 */
export function RecorridoAutomatico() {
  const { abrir } = useRecorrido()

  useEffect(() => {
    const lead = leadIdActual()
    if (!lead) return

    let visto: string | null
    try {
      visto = window.localStorage.getItem(CLAVE_YA_RECORRIDO)
    } catch {
      // Navegación privada con almacenamiento bloqueado: mejor no abrir nada
      // que abrirlo en cada carga de página.
      return
    }
    if (visto === lead) return

    const t = window.setTimeout(() => {
      try {
        window.localStorage.setItem(CLAVE_YA_RECORRIDO, lead)
      } catch {
        return
      }
      abrir()
    }, 700)

    return () => window.clearTimeout(t)
    // `abrir` se recrea en cada render del proveedor; incluirlo reabriría el
    // recorrido en bucle. La intención es "una vez al montar", y eso es.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}

export const TOTAL_PASOS = PASOS.length
